import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Calculator, ArrowRight, Search, CircleDollarSign, Building2, Train, Baby } from 'lucide-react';
import { routingKeyToCounty } from '../../data/routingKeyToCounty';

const countySlugs = Array.from(
  new Set(Object.values(routingKeyToCounty).map((county) => county.toLowerCase()))
).sort();

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const CostOfLivingDiscoverPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredCounties = useMemo(
    () => countySlugs.filter((county) => county.includes(searchTerm.toLowerCase().trim())),
    [searchTerm]
  );

  return (
    <>
      <Helmet>
        <title>Cost of Living Calculator Ireland (2026) | County-by-County</title>
        <meta
          name="description"
          content="Use RentSmart's 2026 cost of living calculators for Irish counties. Compare rent, groceries, childcare, transport and utilities with interactive monthly budget estimates."
        />
        <meta
          name="keywords"
          content="cost of living ireland county, ireland budget calculator, county rent calculator ireland, moving to ireland county budget, cost of living dublin cork galway"
        />
        <link rel="canonical" href="https://rentsmart.ie/cost-of-living" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Cost of Living Calculator Ireland by County',
            description:
              'Interactive county pages estimating monthly cost of living in Ireland for different household profiles.',
            url: 'https://rentsmart.ie/cost-of-living',
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] bg-slate-50 overflow-x-hidden">
        <section className="relative overflow-hidden bg-slate-900 text-white pt-16 pb-20 px-4">
          <div className="absolute top-0 right-0 h-80 w-80 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-cyan-500/20 blur-3xl" />

          <div className="relative max-w-6xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wider mb-6">
              <Calculator className="h-4 w-4" /> 2026 Cost of Living pSEO Hub
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight mb-4">
              Cost of Living Calculator for Irish Counties
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl leading-relaxed mb-8">
              Compare monthly living costs county by county and model your own lifestyle scenario.
              This tool turns static averages into an interactive budget that helps renters, buyers,
              and relocating households make informed moves.
            </p>

            <div className="max-w-xl relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search county (e.g. dublin, cork, galway)"
                className="w-full rounded-xl bg-white text-slate-900 pl-12 pr-4 py-4 shadow-xl outline-none focus:ring-4 focus:ring-cyan-200"
              />
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-10">
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <CircleDollarSign className="h-5 w-5 text-emerald-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Live Monthly Budget</h2>
              <p className="text-sm text-slate-600">Model your expected spend by household setup and lifestyle.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <Building2 className="h-5 w-5 text-sky-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Rent and Housing Context</h2>
              <p className="text-sm text-slate-600">See how housing drives affordability by county.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <Train className="h-5 w-5 text-violet-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Transport Trade-offs</h2>
              <p className="text-sm text-slate-600">Compare pass-based travel with car-dependent scenarios.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <Baby className="h-5 w-5 text-amber-600 mb-2" />
              <h2 className="text-sm font-black text-slate-900 mb-1">Family & Childcare</h2>
              <p className="text-sm text-slate-600">Toggle childcare to stress-test family budgets.</p>
            </div>
          </div>

          {filteredCounties.length === 0 ? (
            <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">
              No counties match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCounties.map((countySlug) => {
                const countyName = toTitleCase(countySlug);
                return (
                  <Link
                    key={countySlug}
                    to={`/cost-of-living/${countySlug}`}
                    className="no-underline group block rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
                  >
                    <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-t-2xl">
                      <h2 className="text-2xl font-black text-slate-900">{countyName}</h2>
                      <p className="text-sm text-slate-600 mt-2">
                        Cost of Living Calculator and housing affordability breakdown.
                      </p>
                    </div>
                    <div className="p-6 flex items-center justify-between text-sm font-bold text-emerald-700">
                      Open Calculator <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default CostOfLivingDiscoverPage;
